import {
  Caption,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eluve/components';

/**
 * Edit-mode input primitives — the form twins of the datatype displays in
 * displays.tsx. Dumb controlled components: views bind them to
 * useFhirEditState and own the immutable resource updates. Empty input
 * maps to undefined so cleared fields diff to JSONPatch removes.
 */

function FieldShell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <Caption color="tertiary">{label}</Caption>
      {children}
    </label>
  );
}

export function StringInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  placeholder?: string;
  type?: 'text' | 'tel' | 'email';
}) {
  return (
    <FieldShell label={label}>
      <Input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value || undefined)}
      />
    </FieldShell>
  );
}

/** FHIR integer primitive; empty clears the field. */
export function IntegerInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: number | undefined;
  onChange: (next: number | undefined) => void;
  placeholder?: string;
}) {
  return (
    <FieldShell label={label}>
      <Input
        type="number"
        min={0}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(event) => {
          if (event.target.value === '') return onChange(undefined);
          const parsed = Number.parseInt(event.target.value, 10);
          if (Number.isFinite(parsed) && parsed >= 0) onChange(parsed);
        }}
      />
    </FieldShell>
  );
}

/** FHIR date primitive (YYYY-MM-DD); native date control. */
export function DateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
}) {
  return (
    <FieldShell label={label}>
      <Input
        type="date"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || undefined)}
      />
    </FieldShell>
  );
}

/**
 * Closed-code select for required-binding FHIR code fields (e.g.
 * administrative gender). Options are the caller's — terminology-backed
 * value sets come later.
 */
export function CodeSelectInput<T extends string>({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: T | undefined;
  onChange: (next: T) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
  placeholder?: string;
}) {
  return (
    <FieldShell label={label}>
      <Select value={value ?? ''} onValueChange={(next) => onChange(next as T)}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder ?? 'Select…'} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
}
